// Format date to ISO string
export const formatDateTimeToISO = (date: Date): string => {
  return date.toISOString();
};

// Format date to display string
export const formatDateTimeDisplay = (date: Date): string => {
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Check if date is in the past
export const isPastDate = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

// Get days in month
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

// Get first day of month (0 = Sunday, 1 = Monday, ...)
export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

// Get calendar days (including prev/next month days)
export const getCalendarDays = (year: number, month: number): Date[] => {
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInMonth = getDaysInMonth(year, month);
  const daysInPrevMonth = getDaysInMonth(year, month - 1);

  const days: Date[] = [];

  // Previous month days
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push(new Date(year, month - 1, daysInPrevMonth - i));
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }

  // Next month days to complete the grid
  const remainingDays = 42 - days.length; // 6 weeks * 7 days
  for (let i = 1; i <= remainingDays; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
};

// Check if two dates are the same day
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Format price to Korean Won
export const formatPrice = (price: number): string => {
  const formatted = new Intl.NumberFormat('ko-KR').format(price);
  return `₩${formatted}`;
};

// Calculate hours between two dates
export const calculateHours = (start: Date, end: Date): number => {
  const diff = end.getTime() - start.getTime();
  return Math.ceil(diff / (1000 * 60 * 60));
};

// Calculate rental price based on weekday/weekend rates
// Pricing policy: 24-hour cycle repeating pattern
export const calculateRentalPrice = (
  startDate: Date,
  endDate: Date,
  weekdayRate: number,
  weekendRate: number,
  fiveHourRate: number = 0
): number => {
  const totalHours = calculateHours(startDate, endDate);

  // Minimum 5 hours
  if (totalHours < 5) {
    return 0;
  }

  // Determine if start date is weekend
  // Weekend: Friday(5), Saturday(6), Sunday(0)
  const startDayOfWeek = startDate.getDay();
  const isWeekend = startDayOfWeek === 5 || startDayOfWeek === 6 || startDayOfWeek === 0;
  const baseRate = isWeekend ? weekendRate : weekdayRate;

  let totalAmount = 0;

  // Calculate in 48-hour cycles
  const full48hCycles = Math.floor(totalHours / 48);
  const remainingHours = totalHours % 48;

  // Add full 48-hour cycle fees
  totalAmount += full48hCycles * baseRate * 2;

  // Calculate remaining hours fee (0~47 hours)
  if (remainingHours > 0) {
    if (remainingHours >= 1 && remainingHours <= 4) {
      // 1~4 hours: 10% per hour
      totalAmount += baseRate * remainingHours * 0.1;
    } else if (remainingHours === 5) {
      // 5 hours: use Wooky API 5-hour rate (fivemoney), fallback to 40% of 24-hour rate
      totalAmount += fiveHourRate > 0 ? fiveHourRate : baseRate * 0.4;
    } else if (remainingHours >= 6 && remainingHours <= 18) {
      // 6~18 hours: 90% of 24-hour rate
      totalAmount += baseRate * 0.9;
    } else if (remainingHours >= 19 && remainingHours <= 24) {
      // 19~24 hours: full 24-hour rate
      totalAmount += baseRate;
    } else if (remainingHours >= 25 && remainingHours <= 33) {
      // 25~33 hours: base rate + 10% per extra hour
      const extraHours = remainingHours - 24;
      totalAmount += baseRate * (1 + extraHours * 0.1);
    } else if (remainingHours >= 34 && remainingHours <= 36) {
      // 34~36 hours: 95% of 48-hour rate
      totalAmount += baseRate * 2 * 0.95;
    } else if (remainingHours >= 37 && remainingHours <= 47) {
      // 37~47 hours: full 48-hour rate
      totalAmount += baseRate * 2;
    }
  }

  return Math.round(totalAmount);
};
